import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormatContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { Button, Stack } from 'src/core/ui';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { posField } from 'src/utils/posFieldUtils';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';

import { usePurchaseCurrencyConversion } from './PurchaseMainDetails.hooks';
import {
  convertToOriginalCurrency,
  convertToTargetCurrency,
} from './PurchaseMainDetails.utils';

export function PurchaseMainDetailsConversionCurrencySelector() {
  const { setValue, getValues, formState } =
    useFormContext<PurchaseOrderDetailsInput>();
  const { getUiCurrency } = useLocalizationContext();
  const {
    isViewingInOriginalCurrency,
    originalCurrency,
    targetCurrency,
    conversionRate,
    isConvertedPurchase,
  } = usePurchaseCurrencyConversion();

  const handleSwitchToTarget = useCallback(() => {
    if (conversionRate == null) return;
    if (!isViewingInOriginalCurrency) return;
    convertToTargetCurrency(
      conversionRate,
      targetCurrency,
      getUiCurrency,
      formState.defaultValues,
      setValue,
      getValues
    );
    setValue('currencyCode', posField(targetCurrency, false));
  }, [
    conversionRate,
    targetCurrency,
    getUiCurrency,
    formState.defaultValues,
    setValue,
    getValues,
    isViewingInOriginalCurrency,
  ]);

  const handleSwitchToOriginal = useCallback(() => {
    if (conversionRate == null) return;
    if (isViewingInOriginalCurrency) return;
    convertToOriginalCurrency(
      conversionRate,
      originalCurrency!,
      getUiCurrency,
      setValue,
      getValues
    );
    setValue('currencyCode', posField(originalCurrency, false));
  }, [
    conversionRate,
    originalCurrency,
    getUiCurrency,
    setValue,
    getValues,
    isViewingInOriginalCurrency,
  ]);

  if (!isConvertedPurchase) {
    return null;
  }

  return (
    <Stack direction="row" gap="s" alignItems="center">
      <Button
        variant={!isViewingInOriginalCurrency ? 'rectangle' : 'textPlain'}
        onClick={handleSwitchToTarget}
        style={{ whiteSpace: 'nowrap' }}
      >
        {targetCurrency}
      </Button>
      <Button
        variant={isViewingInOriginalCurrency ? 'rectangle' : 'textPlain'}
        onClick={handleSwitchToOriginal}
        style={{ whiteSpace: 'nowrap' }}
      >
        <FormatContent
          id={FormatContentId.CurrencyOriginal}
          params={originalCurrency ?? ''}
        />
      </Button>
    </Stack>
  );
}
